import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Divider,
  Badge,
  Button,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const Messages = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    // Poll for new messages every 30 seconds
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/lms/whatsapp/conversations/');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      setLoadingMessages(true);
      const response = await api.get(`/lms/whatsapp/conversations/${conversationId}/messages/`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        messagesContainerRef.current?.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [messages]);

  const handleSelectConversation = async (conversation) => {
    // Fetch full conversation details to get lead_info
    try {
      const response = await api.get(`/lms/whatsapp/conversations/${conversation.id}/`);
      console.log('Conversation details:', response.data);
      console.log('Lead info:', response.data.lead_info);
      console.log('Lead ID:', response.data.lead_id);
      setSelectedConversation(response.data);
    } catch (error) {
      console.error('Error fetching conversation details:', error);
      // Fallback to the conversation from list if detail fetch fails
      setSelectedConversation(conversation);
    }
    
    await fetchMessages(conversation.id);
    
    // Scroll to bottom after messages are loaded
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 200);
    
    // Mark as read
    if (conversation.unread_count > 0) {
      try {
        await api.patch(`/lms/whatsapp/conversations/${conversation.id}/`, {
          unread_count: 0
        });
        // Update local state
        setConversations(prev => 
          prev.map(c => c.id === conversation.id ? {...c, unread_count: 0} : c)
        );
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.contact_phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.last_message_preview?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.integration_info?.phone_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.integration_info?.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.lead_info?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 200px)' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Messages
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage your WhatsApp conversations
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ height: 'calc(100% - 100px)' }}>
        {/* Conversations List */}
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Divider />
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress />
              </Box>
            ) : filteredConversations.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', flex: 1 }}>
                <WhatsAppIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {searchQuery ? 'No conversations found' : 'No conversations yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {!searchQuery && 'Messages will appear here when customers contact you'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ overflow: 'auto', flex: 1 }}>
                {filteredConversations.map((conversation) => (
                  <ListItem
                    key={conversation.id}
                    button
                    selected={selectedConversation?.id === conversation.id}
                    onClick={() => handleSelectConversation(conversation)}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      borderLeft: selectedConversation?.id === conversation.id 
                        ? '3px solid' 
                        : '3px solid transparent',
                      borderLeftColor: selectedConversation?.id === conversation.id 
                        ? 'primary.main' 
                        : 'transparent',
                      backgroundColor: selectedConversation?.id === conversation.id 
                        ? 'rgba(25, 118, 210, 0.08)' 
                        : 'transparent',
                      '&:hover': {
                        bgcolor: selectedConversation?.id === conversation.id 
                          ? 'rgba(25, 118, 210, 0.12)' 
                          : 'action.hover',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        borderLeftColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)',
                        },
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        badgeContent={conversation.unread_count}
                        color="primary"
                        max={99}
                      >
                        <Avatar sx={{ bgcolor: '#25D366' }}>
                          {(conversation.lead_info?.name || conversation.contact_name || conversation.contact_phone)?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2" fontWeight={conversation.unread_count > 0 ? 'bold' : 'normal'} noWrap>
                                {conversation.lead_info?.name || conversation.contact_name || conversation.contact_phone}
                              </Typography>
                              {conversation.integration_info?.phone_number && (
                                <Chip
                                  label={conversation.integration_info.phone_number}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              {conversation.lead_info?.name && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {conversation.contact_phone}
                                </Typography>
                              )}
                              {conversation.integration_info?.display_name && conversation.integration_info.display_name !== conversation.integration_info.phone_number && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  via {conversation.integration_info.display_name}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {formatTime(conversation.last_message_at)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          noWrap
                          fontWeight={conversation.unread_count > 0 ? 'bold' : 'normal'}
                        >
                          {conversation.last_message_preview || 'No messages'}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Message View */}
        <Grid item xs={12} md={8} sx={{ height: '100%' }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Avatar sx={{ bgcolor: '#25D366', mr: 2 }}>
                        {(selectedConversation.lead_info?.name || selectedConversation.contact_name || selectedConversation.contact_phone)?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {selectedConversation.lead_info?.name || selectedConversation.contact_name || selectedConversation.contact_phone}
                          </Typography>
                          {selectedConversation.integration_info?.phone_number && (
                            <Chip
                              label={selectedConversation.integration_info.phone_number}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {selectedConversation.contact_phone}
                        </Typography>
                        {selectedConversation.integration_info?.display_name && selectedConversation.integration_info.display_name !== selectedConversation.integration_info.phone_number && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                            via {selectedConversation.integration_info.display_name}
                          </Typography>
                        )}
                        {(selectedConversation.lead_info || selectedConversation.lead_id) && (
                          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                            {selectedConversation.lead_info ? (
                              <Link
                                component="button"
                                variant="caption"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('Navigating to lead:', selectedConversation.lead_info.id);
                                  if (selectedConversation.lead_info?.id) {
                                    navigate(`/leads/${selectedConversation.lead_info.id}`);
                                  }
                                }}
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  border: 'none',
                                  background: 'none',
                                  padding: 0,
                                  '&:hover': {
                                    color: 'primary.dark',
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                Lead: {selectedConversation.lead_info.name}
                              </Link>
                            ) : selectedConversation.lead_id ? (
                              <Link
                                component="button"
                                variant="caption"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('Navigating to lead ID:', selectedConversation.lead_id);
                                  navigate(`/leads/${selectedConversation.lead_id}`);
                                }}
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  border: 'none',
                                  background: 'none',
                                  padding: 0,
                                  '&:hover': {
                                    color: 'primary.dark',
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                View Lead #{selectedConversation.lead_id}
                              </Link>
                            ) : null}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Messages */}
                {loadingMessages ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box 
                    ref={messagesContainerRef}
                    sx={{ flex: 1, overflow: 'auto', p: 2 }}
                  >
                    {messages.length === 0 ? (
                      <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No messages yet
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {messages.map((message) => (
                          <Box
                            key={message.id}
                            sx={{
                              display: 'flex',
                              justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start',
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                maxWidth: '70%',
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: message.direction === 'outbound' ? '#DCF8C6' : 'grey.100',
                                position: 'relative',
                              }}
                            >
                              {/* Quoted Message (Reply To) */}
                              {message.reply_to && (
                                <Box
                                  sx={{
                                    borderLeft: '3px solid',
                                    borderLeftColor: message.direction === 'outbound' ? '#34B7F1' : '#25D366',
                                    pl: 1,
                                    pr: 1,
                                    pb: 1,
                                    mb: 1,
                                    bgcolor: message.direction === 'outbound' ? 'rgba(52, 183, 241, 0.1)' : 'rgba(37, 211, 102, 0.1)',
                                    borderRadius: '4px 0 0 4px',
                                  }}
                                >
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      color: message.direction === 'outbound' ? '#34B7F1' : '#25D366',
                                      display: 'block',
                                      mb: 0.5
                                    }}
                                  >
                                    {selectedConversation?.integration_info?.display_name || selectedConversation?.integration_info?.phone_number || 'Business'}
                                  </Typography>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontSize: '0.75rem',
                                      color: 'text.secondary',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {message.reply_to.content || '[Message]'}
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Message Content */}
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  mb: 0.5
                                }}
                              >
                                {message.content}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start',
                                  gap: 0.5,
                                  fontSize: '0.7rem',
                                  lineHeight: 1.2
                                }}
                              >
                                {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                {message.direction === 'outbound' && (
                                  <Box component="span" sx={{ fontSize: '0.6rem' }}>
                                    ✓✓
                                  </Box>
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </Box>
                )}

                {/* Message Input - Disabled for now */}
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    To send messages, use WhatsApp templates or initiate conversations from the customer contact in your CRM.
                  </Alert>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <WhatsAppIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Select a conversation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a conversation from the list to view messages
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Messages;
